import { useBookLessonSummaries } from "@/lib/hooks/bookLessons/useBookLessons";
import { type BookScope, BookScopePicker, EMPTY_BOOK_SCOPE, Field } from "./BookScopePicker";
import type { BookLessonRef } from "./types";

export interface BookLessonSelection extends BookScope {
  lesson: number | null;
}

export const EMPTY_BOOK_SELECTION: BookLessonSelection = { ...EMPTY_BOOK_SCOPE, lesson: null };

type BookLessonSummary = ReturnType<typeof useBookLessonSummaries>["bookLessons"][number];

function countRows(summary: BookLessonSummary, type: string): number {
  return summary.rows.filter((row) => row.type === type && !row.subList).length;
}

/** How many words the book lists under each sub-list of its own, so each is counted on its own. */
function countSubLists(summary: BookLessonSummary): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of summary.rows) {
    if (row.subList) counts[row.subList] = (counts[row.subList] ?? 0) + 1;
  }
  return counts;
}

function countLines(summary: BookLessonSummary, kind: string): number {
  return summary.pageLines.filter((line) => line.kind === kind).length;
}

function toRef(summary: BookLessonSummary, seriesSlug: string): BookLessonRef {
  return {
    id: summary.id,
    seriesSlug,
    levelId: summary.levelId,
    componentId: summary.componentId,
    lesson: summary.lesson,
    printedLabel: summary.printedLabel,
    wordCount: countRows(summary, "word"),
    subListCounts: countSubLists(summary),
    grammarCount: countRows(summary, "grammar"),
    grammarExampleCount: countRows(summary, "grammarExamples"),
    passageCount: countLines(summary, "passage"),
    dialogueCount: countLines(summary, "dialogue"),
    pageLineCount: summary.pageLineCount,
  };
}

/**
 * Resolves a selection to the book lesson it names, with the row counts the generator's slide
 * counts are derived from. Null until all four parts are chosen.
 */
export function useBookLessonRef(selection: BookLessonSelection): BookLessonRef | null {
  const { bookLessons } = useBookLessonSummaries(selection.seriesSlug);
  if (selection.seriesSlug === null || selection.lesson === null) return null;

  const found = bookLessons.find(
    (one) =>
      one.levelId === selection.levelId &&
      one.componentId === selection.componentId &&
      one.lesson === selection.lesson
  );
  return found ? toRef(found, selection.seriesSlug) : null;
}

export function BookLessonPicker({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: BookLessonSelection;
  onChange: (value: BookLessonSelection) => void;
}) {
  const { bookLessons, loading: lessonsLoading } = useBookLessonSummaries(value.seriesSlug);
  const book = useBookLessonRef(value);

  // Lessons are numbered per level, so only the chosen level's lessons belong in the list.
  const choices = [
    ...new Map(
      bookLessons
        .filter(
          (one) =>
            (value.levelId === null || one.levelId === value.levelId) &&
            (value.componentId === null || one.componentId === value.componentId)
        )
        .map((one) => [one.lesson, one.printedLabel])
    ),
  ].sort(([a], [b]) => a - b);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <BookScopePicker
          idPrefix={idPrefix}
          value={value}
          onChange={(scope) => onChange({ ...scope, lesson: null })}
        />
        <Field
          id={`${idPrefix}-lesson`}
          label="Lesson"
          value={value.lesson === null ? null : String(value.lesson)}
          placeholder={lessonsLoading ? "Loading…" : "Select"}
          disabled={value.levelId === null || value.componentId === null || lessonsLoading}
          options={choices.map(([lesson, label]) => ({ value: String(lesson), label }))}
          onChange={(lesson) =>
            onChange({ ...value, lesson: lesson === null ? null : Number(lesson) })
          }
        />
      </div>
      {book && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {book.wordCount} words · {book.grammarCount} grammar points · {book.passageCount} passage
          lines · {book.dialogueCount} dialogue lines
        </p>
      )}
    </div>
  );
}
